const STORAGE_KEY = "onboarding_session"

const SESSION_TTL_MS = 60 * 60 * 1000

export type OnboardingSessionButton = "setup_moderation" | "try_ai_shield"

export interface OnboardingSession {
  token: string
  button: OnboardingSessionButton
  startedAt: number
}

let memorySession: OnboardingSession | null = null

const listeners = new Set<() => void>()

const notify = () => {
  for (const listener of listeners) listener()
}

export const isOnboardingButton = (value: unknown): value is OnboardingSessionButton =>
  value === "setup_moderation" || value === "try_ai_shield"

const isFresh = (session: OnboardingSession): boolean => Date.now() - session.startedAt <= SESSION_TTL_MS

const readStorage = (): OnboardingSession | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<OnboardingSession>
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.startedAt !== "number" ||
      !isOnboardingButton(parsed.button)
    ) {
      return null
    }

    return { token: parsed.token, button: parsed.button, startedAt: parsed.startedAt }
  } catch {
    return null
  }
}

export const generateToken = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `ob-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`
}

export const startOnboardingSession = (button: OnboardingSessionButton): OnboardingSession => {
  const session: OnboardingSession = {
    token: generateToken(),
    button,
    startedAt: Date.now()
  }

  memorySession = session
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // ignore
  }

  notify()
  return session
}

export const getActiveOnboardingSession = (): OnboardingSession | null => {
  const session = readStorage() ?? memorySession
  if (!session) return null

  if (!isFresh(session)) {
    memorySession = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
    return null
  }

  return session
}

export const getActiveOnboardingSessionToken = (): string | null => {
  const session = readStorage() ?? memorySession
  if (!session || !isFresh(session)) return null
  return session.token
}

export const clearOnboardingSession = (): void => {
  memorySession = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  notify()
}

export const subscribeOnboardingSession = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
